import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import {
  CASE_STATUS_KEYS,
  CASE_STATUS_TONE,
  DEFAULT_CLIENT_CASE_SORT,
  INPUT_TYPE_LABEL,
  STAGE_LABEL,
  canOpenView,
  clientCaseFilterNarrows,
  doctorCasesExportName,
  isActiveCase,
  listedCaseStatus,
  patientCode,
  readClientCaseFilter,
  writeClientCaseFilter,
  type ClientCaseDue,
  type ClientCaseFilter,
  type ClientCaseSort,
  type ClientCaseStatus,
  type ColumnSort,
  type InputType,
  type JobOrder,
  type ListedCaseStatus,
  type PatientDoctor,
  type SortPreset,
  type Tone,
  caseFinished,
} from '@/domain';
import type { ClientCasesPage } from '@/application/ports/ClientRepository';
import {
  CLIENT_CASES_PAGE_SIZE,
  useDoctorCases,
  useExportDoctorCases,
  usePatient,
  usePatientCases,
} from '@/application/usecases/useClients';
import { useSession } from '@/application/usecases/useSession';
import { PAGE_SIZES, useTablePage } from '@/application/usecases/useTablePage';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { downloadBlob } from '@/shared/format/download';
import { formatDay } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { CaseTable } from '@/ui/data/CaseTable';
import { DeliveryDate } from '@/ui/data/DeliveryDate';
import { FilterBar, type FilterDef } from '@/ui/data/FilterBar';
import type { Column } from '@/ui/data/ResponsiveTable';
import { CASE_STATUS_LABEL } from '@/ui/data/shippingLabels';
import { SortSelect } from '@/ui/data/SortSelect';
import { StatusWithNote } from '@/ui/data/StatusDot';
import { orderColumns } from '@/ui/data/orderColumns';
import { EmptyState } from '@/ui/feedback/EmptyState';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { CloseIcon, OrdersIcon } from '@/ui/icons';
import { usePhoneLayout } from '@/ui/layout/usePhoneLayout';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { useCaseInPlace } from '@/features/orders/useCaseInPlace';
import { ClientLink } from './ClientLink';
import {
  CountRow,
  ExportButton,
  FilterSheet,
  FiltersButton,
  LoadFailed,
  None,
} from './ClientTableParts';
import { asServed, columnOfSort, sortOfColumn } from './clientTables';
import { useAddressParams, useAddressSearch } from './useAddressParams';
import styles from './ClientCases.module.css';

/** The doctor whose cases a doctor's page lists. */
export interface CasesDoctor {
  id: string;
  /** The ID, `DR-0012`, which names the exported file. */
  code: string;
  name: string;
}

/** The patient whose cases a patient's page lists, with the doctors those cases are with. */
export interface CasesPatient {
  id: string;
  name: string;
  doctors: readonly PatientDoctor[];
}

/**
 * A doctor's cases: the Cases tab of the doctor's page. The request of
 * 6 October 2026 (fifth), section 4. Searched by job order number or patient,
 * narrowed to one patient from the Patients tab, and exported.
 */
export function DoctorCases({ doctor }: { doctor: CasesDoctor }) {
  const address = useCasesAddress('doctorCases');
  // A doctor's cases are never narrowed by doctor: an address that says so is not sent.
  const cases = useDoctorCases(doctor.id, { ...address.asked, doctorId: undefined });
  const exporter = useExportDoctorCases();
  const { filter } = address;
  const total = cases.data?.total ?? 0;

  return (
    <CasesTable
      owner="doctor"
      name={doctor.name}
      cases={cases}
      address={address}
      exportAction={
        <ExportButton
          pending={exporter.isPending}
          onExport={() =>
            exporter.mutate(
              { doctorId: doctor.id, filter: { ...filter, doctorId: undefined } },
              {
                onSuccess: (blob) => {
                  downloadBlob(blob, doctorCasesExportName(doctor.code));
                  toast(strings.clientCases.casesExported(total));
                },
                onError: () => toast(strings.clientCases.exportFailed),
              },
            )
          }
        />
      }
    />
  );
}

/**
 * A patient's cases, on the patient's page: the same table, search, filters
 * and order, with the doctor in place of the patient, since a patient's cases
 * are several doctors'. Narrowed to one of those doctors from the bar. Not
 * exported: the request asks for the export on the doctor's page.
 */
export function PatientCases({ patient }: { patient: CasesPatient }) {
  const address = useCasesAddress('patientCases');
  const cases = usePatientCases(patient.id, { ...address.asked, patientId: undefined });

  return (
    <CasesTable
      owner="patient"
      name={patient.name}
      doctors={patient.doctors}
      cases={cases}
      address={address}
    />
  );
}

/** As much of a query as the table reads. */
interface CasesQuery {
  data?: ClientCasesPage;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => unknown;
}

/** The cases filter of a page, as its address holds it, and the ways to change it. */
interface CasesAddress {
  /** What the address says: the search, the filters, the order and the page. */
  filter: ClientCaseFilter;
  /** What the server is asked: the same, a page of the size this device shows. */
  asked: ClientCaseFilter;
  page: number;
  pageSize: number;
  /** A change to the search, a filter or the order. The list starts again from its first page. */
  change: (changes: Partial<ClientCaseFilter>) => void;
  goTo: (page: number) => void;
  choosePageSize: (size: number) => void;
}

/**
 * The cases filter kept in the page's address. Section 4 of the request: all
 * of it "server-side", and remembered "while the user moves between cases".
 *
 * The search, every filter, the order and the page are query keys of the
 * address (`readClientCaseFilter` and `writeClientCaseFilter` in the domain,
 * which leave every other key alone), changed in place. How many rows make a
 * page is not there: that is how this person reads every table on this device,
 * remembered per table as it is everywhere else.
 */
function useCasesAddress(tableKey: string): CasesAddress {
  const [params, replace] = useAddressParams();
  const sizing = useTablePage(tableKey, CLIENT_CASES_PAGE_SIZE);
  const { pageSize, choose } = sizing;

  return useMemo(() => {
    const filter: ClientCaseFilter = { ...readClientCaseFilter(params), pageSize: undefined };
    const page = filter.page ?? 1;
    const write = (next: (current: ClientCaseFilter) => ClientCaseFilter) =>
      replace((current) =>
        writeClientCaseFilter(current, {
          ...next(readClientCaseFilter(current)),
          pageSize: undefined,
        }),
      );

    return {
      filter,
      asked: { ...filter, page, pageSize },
      page,
      pageSize,
      // Page three of the old list is nothing in the new one.
      change: (changes) => write((current) => ({ ...current, ...changes, page: undefined })),
      goTo: (next) => write((current) => ({ ...current, page: next })),
      choosePageSize: (size) => {
        choose(size);
        write((current) => ({ ...current, page: undefined }));
      },
    };
  }, [params, replace, pageSize, choose]);
}

/** What the Status column and its filter call a case: the eight every list uses, and Archived. */
const STATUS_LABEL: Record<ListedCaseStatus, string> = {
  ...CASE_STATUS_LABEL,
  archived: strings.clientCases.archived,
};

/** Archived is off every list of work in hand: neither late nor done, so it carries no colour. */
const STATUS_TONE: Record<ListedCaseStatus, Tone> = { ...CASE_STATUS_TONE, archived: 'idle' };

/** The orders the server sorts by, as the bar offers them. Newest first is the server's own. */
const SORTS: readonly SortPreset<JobOrder>[] = [
  { key: '-createdAt', label: strings.clientCases.sortNewest },
  { key: 'createdAt', label: strings.clientCases.sortOldest },
  { key: 'deliveryDate', label: strings.clientCases.sortDueSoonest },
  { key: '-deliveryDate', label: strings.clientCases.sortDueLatest },
  { key: 'status', label: strings.clientCases.sortStatus },
  { key: '-status', label: strings.clientCases.sortStatusLast },
];

/** The columns whose header asks the server for an order, by the wire's word for each. */
const SERVER_SORTED = ['createdAt', 'deliveryDate', 'status'] as const;
type ServerSorted = (typeof SERVER_SORTED)[number];

function isServerSorted(key: string): key is ServerSorted {
  return (SERVER_SORTED as readonly string[]).includes(key);
}

const ALL = 'all';

/**
 * The cases of one doctor or one patient, in the one case table. The request
 * of 6 October 2026 (second), item 4, and the request of 6 October 2026
 * (fifth), section 4, which made it a table to manage a doctor with hundreds
 * of cases by.
 *
 * Every case the record has, the ones in hand, the ones that have left and the
 * archived; not the deleted. Each row is the job order with the urgent dot and
 * the redo mark, the patient (or, on a patient's page, the doctor), the case
 * type over its item, the department and the technician, the day it was booked
 * and the day it is due, and its status in the words every list uses, with
 * Archived beside them. The name the page is about is not a column.
 *
 * The search, the filters, the order and the page are asked of the server and
 * kept in the address, so the count is of every case that matches and a reload
 * shows the table as it was. Created, Due and Status are ordered by the server
 * from their headers as from the bar; the other headers order the page in
 * hand, as on every case table.
 *
 * A row opens the case in place, over this page (`useCaseInPlace`), for a
 * reader who holds a screen that shows a case; for anybody else a row is a
 * line to read, as it always was.
 */
function CasesTable({
  owner,
  name,
  doctors = [],
  cases,
  address,
  exportAction,
}: {
  /** Whose page this is, and so which of the two names is the column and which the search. */
  owner: 'doctor' | 'patient';
  /** The record's name, for the table's caption. */
  name: string;
  /** A patient's doctors, to narrow the patient's cases to one of them. */
  doctors?: readonly PatientDoctor[];
  cases: CasesQuery;
  address: CasesAddress;
  /** What stands at the end of the count: the export, on a doctor's page. */
  exportAction?: ReactNode;
}) {
  const navigate = useNavigate();
  const headingId = useId();
  const phone = usePhoneLayout();
  const place = useCaseInPlace();
  const views = useSession().data?.views ?? [];
  const { filter, change, page, pageSize } = address;
  // The columns as this reader laid them out, kept on their account. The request of 7 October 2026, section 3.
  const layout = useTableLayout(owner === 'doctor' ? 'doctorCases' : 'patientCases');

  const search = useAddressSearch(filter.q, (q) => change({ q }));
  const [sheetOpen, setSheetOpen] = useState(false);
  // Remounts the bar on Reset: its date control remembers the preset it was set to.
  const [resets, setResets] = useState(0);
  // A header that orders the page in hand, not the list: Job order, and the name beside it.
  const [column, setColumn] = useState<ColumnSort | null>(null);

  const sort = filter.sort ?? DEFAULT_CLIENT_CASE_SORT;
  const total = cases.data?.total ?? 0;
  const rows = cases.data?.items ?? [];
  const narrowed = clientCaseFilterNarrows(filter);
  // The three columns the server orders by leave the page as it arrived.
  const served = asServed(rows, (order) => order.jobOrderNo, sort.startsWith('-'));

  /*
   * A page past the end, from an address made when the list was longer or on a
   * device that shows more rows to a page: back to the first, rather than an
   * empty table over a count that says there are cases.
   */
  const pastTheEnd = cases.data !== undefined && rows.length === 0 && total > 0 && page > 1;
  const { goTo } = address;
  useEffect(() => {
    if (pastTheEnd) goTo(1);
  }, [pastTheEnd, goTo]);

  // The patient a doctor's cases are narrowed to, by name, for the chip.
  const onlyPatient = usePatient(owner === 'doctor' ? filter.patientId : undefined);

  function reset() {
    search.clear();
    setResets((count) => count + 1);
    // Everything that narrows goes; the order is how the list is read, and stays.
    change({
      q: undefined,
      status: undefined,
      from: undefined,
      to: undefined,
      caseType: undefined,
      urgent: undefined,
      due: undefined,
      patientId: undefined,
      doctorId: undefined,
    });
  }

  /** The sheet's own Reset: its filters, and not the search or the patient beside it. */
  function resetSheet() {
    setResets((count) => count + 1);
    change({
      status: undefined,
      from: undefined,
      to: undefined,
      caseType: undefined,
      urgent: undefined,
      due: undefined,
      doctorId: undefined,
    });
  }

  const searchFilter: FilterDef = {
    kind: 'search',
    label: strings.clientCases.search,
    placeholder:
      owner === 'doctor' ? strings.clientCases.searchByPatient : strings.clientCases.searchByDoctor,
    value: search.typed,
    onChange: search.type,
  };

  const narrowing: FilterDef[] = [
    {
      /*
       * All, the cases in hand together, each status the column can show, and
       * Archived: the client asked for "status (including active only)", and a
       * filter that cannot ask for a word the column shows is the defect
       * section 6 of the request of 16 September 2026 was about.
       */
      kind: 'select',
      label: strings.clientCases.status,
      value: filter.status ?? ALL,
      options: [
        { value: ALL, label: strings.clientCases.allStatuses },
        { value: 'active', label: strings.clientCases.activeOnly },
        ...CASE_STATUS_KEYS.map((key) => ({ value: key, label: STATUS_LABEL[key] })),
        { value: 'archived', label: strings.clientCases.archived },
      ],
      onChange: (next) =>
        change({ status: next === ALL ? undefined : (next as ClientCaseStatus) }),
    },
    {
      // The day the case was booked, both ends included.
      kind: 'dateRange',
      label: strings.clientCases.created,
      from: filter.from ?? '',
      to: filter.to ?? '',
      onChange: (range) =>
        change({
          from: range.from === '' ? undefined : range.from,
          to: range.to === '' ? undefined : range.to,
        }),
    },
    {
      kind: 'select',
      label: strings.clientCases.caseType,
      value: filter.caseType ?? ALL,
      options: [
        { value: ALL, label: strings.clientCases.anyCaseType },
        ...(Object.keys(INPUT_TYPE_LABEL) as InputType[]).map((key) => ({
          value: key,
          label: INPUT_TYPE_LABEL[key],
        })),
      ],
      onChange: (next) => change({ caseType: next === ALL ? undefined : (next as InputType) }),
    },
    {
      kind: 'toggle',
      label: strings.clientCases.urgentOnly,
      value: filter.urgent === true,
      onChange: (next) => change({ urgent: next ? true : undefined }),
    },
    {
      // Both only among the cases in hand: one that has left is late on nobody's bench.
      kind: 'select',
      label: strings.clientCases.due,
      value: filter.due ?? ALL,
      options: [
        { value: ALL, label: strings.clientCases.anyDue },
        { value: 'today', label: strings.clientCases.dueToday },
        { value: 'overdue', label: strings.clientCases.overdue },
      ],
      onChange: (next) => change({ due: next === ALL ? undefined : (next as ClientCaseDue) }),
    },
    // One doctor's cases among a patient's, where the patient has been to more than one.
    ...(owner === 'patient' && doctors.length > 1
      ? [
          {
            kind: 'select' as const,
            label: strings.clientCases.doctor,
            value: filter.doctorId ?? ALL,
            options: [
              { value: ALL, label: strings.clientCases.allDoctors },
              // With the ID: two doctors may share a name.
              ...doctors.map((doctor) => ({
                value: doctor.id,
                label: `${doctor.name} (${doctor.code})`,
              })),
            ],
            onChange: (next: string) => change({ doctorId: next === ALL ? undefined : next }),
          },
        ]
      : []),
  ];

  // How many of the sheet's filters are on: a range of days is one filter, not two.
  const filtersOn = [
    filter.status,
    filter.from ?? filter.to,
    filter.caseType,
    filter.urgent,
    filter.due,
    owner === 'patient' ? filter.doctorId : undefined,
  ].filter((value) => value !== undefined).length;

  const columns: Column<JobOrder>[] = [
    {
      /*
       * What the app calls the case type, a digital scan or an impression,
       * with the item under it, quieter: the two answer "what is this case"
       * together. Section 4 of the request, and decision 7 of its design.
       */
      key: 'caseType',
      label: strings.clientCases.colCaseType,
      render: (order) => (
        <span className={styles.stack}>
          <span>{order.input == null ? <None /> : INPUT_TYPE_LABEL[order.input]}</span>
          <span className={styles.under}>
            {order.product == null || order.product === '' ? <None /> : order.product}
          </span>
        </span>
      ),
    },
    {
      // The bench the case is at. A case that has left the lab is at none.
      key: 'department',
      label: strings.clientCases.colDepartment,
      render: (order) =>
        order.currentStage === null ? <None /> : STAGE_LABEL[order.currentStage],
    },
    {
      key: 'technician',
      label: strings.clientCases.colTechnician,
      render: (order) =>
        order.holderName === null ? <None /> : <span dir="auto">{order.holderName}</span>,
    },
    {
      key: 'createdAt',
      label: strings.clientCases.colCreated,
      nowrap: true,
      sortable: true,
      sortValue: served,
      render: (order) =>
        order.createdAt === undefined ? (
          <None />
        ) : (
          <span className={styles.day}>{formatDay(order.createdAt)}</span>
        ),
    },
    {
      /*
       * In the tone its date reads in everywhere while the case is in hand. A
       * case that has left is late on nobody's bench, so its date is only a date.
       */
      key: 'deliveryDate',
      label: strings.clientCases.colDue,
      nowrap: true,
      sortable: true,
      sortValue: served,
      render: (order) =>
        isActiveCase(order) ? (
          <DeliveryDate date={order.deliveryDate} finished={caseFinished(order)} />
        ) : (
          <span className={styles.day}>{order.deliveryDate}</span>
        ),
    },
    // What the rows carry and the table does not show as it comes: a header of these sorts the page in hand.
    // The request of 8 October 2026 (second).
    ...orderColumns(['origin', 'paidAt', 'redoOf']),
  ];

  const empty = narrowed ? (
    <EmptyState
      icon={<OrdersIcon />}
      heading={strings.clientCases.noMatch}
      detail={strings.clientCases.noMatchDetail}
      action={<Button onClick={reset}>{strings.clientCases.reset}</Button>}
    />
  ) : (
    <EmptyState
      icon={<OrdersIcon />}
      heading={strings.clients.noCases}
      detail={
        owner === 'doctor' ? strings.clients.noCasesOfDoctor : strings.clients.noCasesOfPatient
      }
      // The next step, for whoever may take it: a case is booked in at intake.
      action={
        canOpenView(views, 'intake') ? (
          <Button onClick={() => void navigate('/intake')}>{strings.clientCases.bookCase}</Button>
        ) : undefined
      }
    />
  );

  return (
    <section aria-labelledby={owner === 'patient' ? headingId : undefined}>
      {/* A doctor's cases are a tab, which names them; a patient's stand under their own heading. */}
      {owner === 'patient' && (
        <h3 id={headingId} className={styles.heading}>
          {strings.clients.casesHeading}
        </h3>
      )}

      <div className={styles.tools}>
        <FilterBar
          key={resets}
          // On a phone this keeps the search alone on the page, and the rest is in the sheet.
          foldOnPhone={false}
          label={strings.clientCases.filtersLabel}
          filters={phone ? [searchFilter] : [searchFilter, ...narrowing]}
          sort={
            <SortSelect
              presets={SORTS}
              // A header ordering the page in hand is no order of the list's: the control says so.
              value={column === null ? sort : ''}
              onChange={(key) => {
                setColumn(null);
                change({ sort: key as ClientCaseSort });
              }}
            />
          }
          actions={
            phone ? (
              <FiltersButton on={filtersOn} open={sheetOpen} onOpen={() => setSheetOpen(true)} />
            ) : undefined
          }
        />
      </div>

      {owner === 'doctor' && filter.patientId !== undefined && (
        <p className={styles.chip}>
          <span className={styles.chipLabel}>{strings.clientCases.onlyPatient}</span>
          <span className={styles.chipName} dir="auto">
            <ClientLink
              kind="patient"
              id={filter.patientId}
              // The record's name; until it arrives, the name its first case carries, or the ID.
              name={
                onlyPatient.data?.name ?? rows[0]?.patientName ?? patientCode(filter.patientId)
              }
            />
          </span>
          <button
            type="button"
            className={styles.chipRemove}
            aria-label={strings.clientCases.everyPatient}
            title={strings.clientCases.everyPatient}
            onClick={() => change({ patientId: undefined })}
          >
            <CloseIcon size={16} />
          </button>
        </p>
      )}

      {cases.isError && <LoadFailed error={cases.error} onRetry={() => void cases.refetch()} />}

      {cases.isPending && <SkeletonRows rows={4} label={strings.clients.loadingCases} />}

      {!cases.isPending && !cases.isError && (
        <>
          <CountRow
            total={total}
            counted={strings.clientCases.casesCounted(total)}
            narrowed={narrowed}
            onReset={reset}
            action={exportAction}
          />

          {rows.length === 0 ? (
            // A page past the end is on its way back to the first: not an empty list.
            !pastTheEnd && empty
          ) : (
            <CaseTable
              customize={layout}
              without={owner}
              columns={columns}
              rows={rows}
              sort={column ?? columnOfSort(sort)}
              onSortChange={(next) => {
                if (next === null) {
                  // "Default order", from the phone's own control: newest first again.
                  setColumn(null);
                  change({ sort: undefined });
                } else if (isServerSorted(next.key)) {
                  setColumn(null);
                  change({ sort: sortOfColumn(next) as ClientCaseSort });
                } else {
                  // Job order and the name: the page in hand, as on every case table.
                  setColumn(next);
                }
              }}
              identity={(order) => ({
                jobOrderNo: order.jobOrderNo,
                urgent: order.urgent,
                redo: order.redoOf !== null,
                held: order.hold.held,
                dentistName: order.dentistName,
                patientName: order.patientName,
              })}
              status={(order) => {
                const key = listedCaseStatus(order);
                return (
                  // Why it is held, under the word that says it is.
                  <StatusWithNote tone={STATUS_TONE[key]} note={order.hold.held ? order.hold.reason : null}>
                    {STATUS_LABEL[key]}
                  </StatusWithNote>
                );
              }}
              statusSortValue={served}
              rowKey={(order) => order.jobOrderNo}
              onRowClick={place.mayOpen ? place.open : undefined}
              caption={strings.clients.casesOf(name)}
              paging={{
                page,
                pageSize,
                total,
                sizes: PAGE_SIZES,
                onPage: address.goTo,
                onPageSize: address.choosePageSize,
                label: strings.table.pagesLabel,
              }}
            />
          )}
        </>
      )}

      {sheetOpen && (
        <FilterSheet
          barKey={resets}
          filters={narrowing}
          on={filtersOn}
          show={strings.clientCases.showCases(total)}
          onReset={resetSheet}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </section>
  );
}
